import { Clock, Loader2, Plus, Trash2, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTranslation } from '@/hooks/use-translation'
import type { AgentSession } from '../types/agent.types'

type Props = {
  onClose: () => void
  onNewChat: () => void
  sessions: AgentSession[]
  onSelectSession: (id: string) => void
  onDeleteSession: (id: string) => void
  sessionsLoading: boolean
}

function getRelativeTime(dateStr: string, t: ReturnType<typeof useTranslation>): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then

  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return t('agent.time.justNow')

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('agent.time.minutesAgo', { count: minutes })

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('agent.time.hoursAgo', { count: hours })

  const days = Math.floor(hours / 24)
  if (days < 7) return t('agent.time.daysAgo', { count: days })

  const weeks = Math.floor(days / 7)
  if (weeks < 5) return t('agent.time.weeksAgo', { count: weeks })

  const months = Math.floor(days / 30)
  return t('agent.time.monthsAgo', { count: months })
}

export function AgentChatHeader({
  onClose,
  onNewChat,
  sessions,
  onSelectSession,
  onDeleteSession,
  sessionsLoading,
}: Props) {
  const t = useTranslation()

  return (
    <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between shrink-0">
      <div>
        <h2 className="text-sm font-semibold text-card-foreground">{t('agent.header.title')}</h2>
        <p className="text-xs text-muted-foreground">{t('agent.header.subtitle')}</p>
      </div>
      <div className="flex items-center gap-1">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-card-foreground hover:bg-muted transition-colors duration-150"
              aria-label="Session history"
            >
              <Clock className="w-4 h-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-2 bg-card border border-border">
            <p className="text-xs font-medium text-muted-foreground px-2 py-1">
              {t('agent.header.recentChats')}
            </p>
            {sessionsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            ) : sessions.length === 0 ? (
              <p className="text-xs text-muted-foreground px-2 py-3 text-center">
                {t('agent.header.noChats')}
              </p>
            ) : (
              <ul className="space-y-0.5 max-h-60 overflow-y-auto">
                {sessions.map((session) => (
                  <li
                    key={session.id}
                    className="group flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-muted transition-colors duration-150"
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => onSelectSession(session.id)}
                    >
                      <p className="text-sm text-card-foreground truncate">
                        {session.title ?? t('agent.header.untitled')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getRelativeTime(session.updatedAt, t)}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => onDeleteSession(session.id)}
                      className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-destructive transition-all duration-150"
                      aria-label="Delete session"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </PopoverContent>
        </Popover>

        <button
          type="button"
          onClick={onNewChat}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-card-foreground hover:bg-muted transition-colors duration-150"
          aria-label="New chat"
        >
          <Plus className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-card-foreground hover:bg-muted transition-colors duration-150"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
